import React from 'react';
import { FormattedMessage, IntlShape } from 'react-intl';
import * as mobxReact from 'mobx-react';
import * as mobx from 'mobx';
import { OEToggle } from '../../../../../../global/oe-toggle/oe-toggle';
import Text from '../../../../../../global/text/text';
import { FormField } from '../../../../../../global/form/form-field/form-field';
import { RichTextEditor } from '../../../../../../global/rich-text-editor';
import './general-registration.presentation.scss';
import { IRegistrationType, RegistrationTypeValue } from '../../../../../../../api/registration-configuration.dao';
import { ONLY_SPACES_REG_EXP } from '../../../../../../../constants/regex.constants';
import GeneralRegistrationVM from './general-registration.viewmodel';
import { hasPermission } from '../../../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../../../api/roles.dao';
import DefaultButton from '../../../../../../global/default-button/default-button';
import GeneralRegistrationSettings from './general-registration-settings/general-registration-settings';
import { EnablementStatus } from '../../../../../../../constants/common.constants';
import { InfoPopover } from '../../../../../../global/info-popover/info-popover';
import { REGISTRATION_APPROVAL_INFO_TEXT } from '../../../../../../../constants/registration-approval.constants';

interface IWebsiteGeneralRegistrationPresentation {
    vm: GeneralRegistrationVM;
    intl: IntlShape;
}

const GeneralRegistrationView = mobxReact.observer(({ vm, intl }: IWebsiteGeneralRegistrationPresentation) => {

    const registrationLabel = intl.formatMessage({
        id: `oe.website.registrationIs`,
        description: 'Label text',
        defaultMessage: 'Registration is',
    });

    const registrationType: IRegistrationType[] = [
        {
            value: RegistrationTypeValue.OPEN,
            textInUI: intl.formatMessage({
                id: 'oe.website.registrationType.OPEN',
                description: 'Label text',
                defaultMessage: 'Open',
            })
        },
        {
            value: RegistrationTypeValue.CONTROLLED,
            textInUI: intl.formatMessage({
                id: 'oe.website.registrationType.CONTROLLED',
                description: 'Label text',
                defaultMessage: 'Controlled',
            })
        }
    ];

    return (
        <div className='oe-form website-custom__form'>
            <div className='website-custom__checkbox-label m-bottom-0px'>
                <OEToggle isSelected={vm.registrationAttendeeEnabled} onClick={vm.toggleAttendeeRegistration} />
                <Text
                    message={`${registrationLabel} ${vm.getRegistrationEnabled() ? EnablementStatus.ENABLED : EnablementStatus.DISABLED}`} />
            </div>
            <div className='website-custom__checkbox-label m-top-15px m-bottom-0px'>
                <Text
                    message='Click to'
                    id='oe.website.clickTo'
                    description='Label text'
                />
                <div className='text-blue cursor-point link' onClick={vm.showFormRegistrationFormPopup}>
                    <Text
                        message='Configure Registration Form'
                        id='oe.passport.сonfigureRegistrationForm'
                        description='Button text'
                    />
                </div>
            </div>
            {hasPermission(Permission.TRIAGED_REGISTRATION) &&
                <div className='website-custom__checkbox-label m-top-15px m-bottom-0px'>
                    <OEToggle
                        data-attr='triaged-registration'
                        isSelected={vm.getTriagedRegistration()}
                        onClick={vm.toggleTriagedRegistration}
                    />
                    <Text
                        message={vm.getTriagedRegistrationText(intl)}
                    />
                </div>
            }
            {vm.getTriagedRegistration() && !vm.getTriagedQuestionsCreated() &&
                <div className='website-custom__checkbox-label m-top-30px'>
                    <DefaultButton
                        disabled={
                            vm.generalRegistrationFormOptions.getIsSaving()
                        }
                        text='Create a Triage Question'
                        modifier='slim'
                        onClick={vm.onCreateTriagedQuestion}
                    />
                </div>
            }
            <div className='website-custom__checkbox-label m-top-15px m-bottom-0px'>
                <OEToggle
                    isSelected={vm.getAsLandingPage()}
                    onClick={vm.setAsLandingPage}
                    isDisabled={!vm.registrationAttendeeEnabled}
                />
                <Text
                    message={'Registration as Landing page'}
                    id={'oe.website.registrationAsLandingPag'}
                    description={'Label text'}
                />
            </div>
            {vm.getTriagedRegistration()
                ? <GeneralRegistrationSettings
                    intl={intl}
                    vm={vm}
                    registrationType={registrationType}
                />
                : <>
                    <div className='website-custom__checkbox-label column m-top-15px m-bottom-0px'>
                        <Text
                            message='Registration Type'
                            id='oe.website.registrationType'
                            description='Label text'
                        />
                        <select
                            className='oe-form-field__control'
                            value={vm.getRegistrationType('')}
                            onChange={e => vm.setRegistrationType(e.target.value as RegistrationTypeValue, '')}
                            disabled={!vm.registrationAttendeeEnabled}
                        >
                            {
                                registrationType.map(({ value, textInUI }) => <option value={value}
                                    key={value}>{textInUI}</option>)
                            }
                        </select>
                    </div>
                    <div className='website-custom__checkbox-label m-top-15px m-bottom-0px'>
                        <OEToggle
                            isSelected={vm.getCalendarButton('')}
                            onClick={() => vm.toggleCalendarButtonLabel('')}
                            isDisabled={!vm.registrationAttendeeEnabled}
                        />
                        <Text
                            message={'Show "Add to Calendar" button'}
                            id={'oe.website.showAddToCalendarButton'}
                            description={'Label text'}
                        />
                    </div>
                    <div className='website-custom__checkbox-label m-top-15px m-bottom-0px'>
                        <OEToggle
                            data-testid='auto-sign-in'
                            isSelected={vm.getAutoSignInEnabled('')}
                            onClick={() => vm.toggleAutoSignIn('')}
                            isDisabled={!vm.getRegistrationAttendeeEnabled()}
                        />
                        <Text
                            message='One-Step Registration and Sign In'
                            id='oe.website.autoSignIn'
                            description='Label text'
                        />
                    </div>
                    <span className='oe-form-field__info m-bottom-15px'>
                        {!vm.isRegistrationTypeOpen('') ?
                            <FormattedMessage
                                id='oe.website.registrationType.CONTROLLED.info'
                                description='Input info text'
                                defaultMessage='Turn on to allow attendees who successfully register and belong to an approved domain or are already approved to automatically be logged into Passport.'
                            /> :
                            <FormattedMessage
                                id='oe.website.autoSignIn.info'
                                description='Input info text'
                                defaultMessage='Attendees who successfully register will automatically be logged in to Passport'
                            />
                        }
                    </span>
                    <div className='website-custom__checkbox-label m-bottom-15px'>
                        <OEToggle
                            data-testid='show-open-meetings'
                            isSelected={vm.getShowOpenMeetingsEnabled('')}
                            onClick={() => vm.toggleShowOpenMeetings('')}
                            isDisabled={!vm.registrationAttendeeEnabled}
                        />
                        <Text
                            message='Show Open meetings on Registration Page'
                            id='oe.website.autoSignIn'
                            description='Label text'
                        />
                    </div>
                    {vm.urlParameterRegistrationEnabled && (
                        <div className='website-custom__checkbox-label m-bottom-15px'>
                            <OEToggle
                                data-testid='url-parameter-registration'
                                isSelected={vm.getPassportURLParameterRegistrationEnabled()}
                                onClick={() => vm.setPassportURLParameterRegistrationEnabled()}
                            />
                            <Text
                                message= 'URL Parameter Registration is {suffix}'
                                id='oe.website.urlParameterRegistration'
                                values={{
                                    suffix: vm.getPassportURLParameterRegistrationEnabled()
                                        ? EnablementStatus.ENABLED
                                        : EnablementStatus.DISABLED,
                                }}
                            />
                        </div>
                    )}
                    {vm.registrationApprovalEnabled && (
                        <div className='website-custom__checkbox-label m-bottom-15px'>
                            <OEToggle
                                data-testid='registration-approval'
                                isSelected={vm.getPassportRegistrationApprovalEnabled()}
                                onClick={() => vm.setPassportRegistrationApprovalEnabled()}
                            />
                            <Text
                                message='Registration Approval via API is {suffix}'
                                id='oe.website.registrationApprovalViaAPI'
                                values={{
                                    suffix: vm.getPassportRegistrationApprovalEnabled()
                                        ? EnablementStatus.ENABLED
                                        : EnablementStatus.DISABLED,
                                }}
                                description='Label text'
                            />
                            <InfoPopover className='m-left-8px'>
                                <FormattedMessage {...REGISTRATION_APPROVAL_INFO_TEXT} />
                            </InfoPopover>
                        </div>
                    )}
                    <FormField
                        label={<FormattedMessage
                            defaultMessage='Registration Header'
                            id='oe.website.registrationHeader'
                            description='Label text'
                        />}
                    >
                        <input
                            className='oe-form-field__control'
                            value={vm.defaultSettings.header}
                            type='text'
                            onChange={mobx.action(({ target: { value } }) => vm.setRegistrationHeaderMessage(value, ''))}
                        />
                        {!!vm.getErrorMessage('', 'header') &&
                            <span
                                className='oe-form-field__error'>{vm.getErrorMessage('', 'header')}</span>
                        }

                    </FormField>

                    <FormField
                        label={<FormattedMessage
                            defaultMessage='Registration Instructions'
                            id='oe.website.registrationInstructions'
                            description='Label text'
                        />}
                        className='website-custom__editor'
                    >
                        <RichTextEditor
                            value={vm.defaultSettings.instructions}
                            onChange={(event, editor) => vm.setRegistrationInstructionsMessage(editor.getData(), '')}
                            validationError={vm.getErrorMessage('', 'instructions') || null}
                        />
                    </FormField>
                    <FormField
                        label={<FormattedMessage
                            id='oe.registration.submitMessage'
                            defaultMessage='Registration Submitted Message'
                            description='Label text'
                        />}
                        className='website-custom__editor'
                    >
                        <RichTextEditor
                            value={vm.getRegistrationSubmitMessage('')}
                            onChange={(event, editor) => vm.setRegistrationSubmitMessage(editor.getData(), '')}
                            validationError={vm.getErrorMessage('', 'registrationSubmittedMessage') || null}
                        />
                    </FormField>
                    <FormField
                        label={<FormattedMessage
                            id='oe.registration.pendingDeniedMessage'
                            defaultMessage='Registration Pending & Denied Message'
                            description='Label text'
                        />}
                        className='website-custom__editor'
                        infoIconText={intl.formatMessage({
                            id: 'oe.registration.pendingDeniedMessage.info',
                            defaultMessage: 'This message will be displayed to attendees when their registration is pending or denied.',
                            description: 'Info text'
                        })}
                    >
                        <RichTextEditor
                            value={vm.getPendingDeniedMessage('')}
                            onChange={(event, editor) => vm.setPendingDeniedMessage(editor.getData(), '')}
                            validationError={vm.getErrorMessage('', 'pendingDeniedMessage') || null}
                        />
                    </FormField>
                </>
            }

            <FormField className='blocked-domains-form-field' label={<FormattedMessage
                defaultMessage='Blocked Domains'
                id='oe.registration.blockedDomains'
                description='Input label'
            />}>
                <textarea
                    className='oe-form-field__control'
                    value={vm.getBlockedDomains()}
                    onChange={mobx.action(({ target: { value } }) => vm.setBlockedDomains(value))}
                    onBlur={mobx.action(({ target: { value } }) => {
                        value.replace(ONLY_SPACES_REG_EXP, '');
                        vm.setBlockedDomainsError(intl);
                    })}
                />
                {vm.getBlockedDomainsError() &&
                    <div className='oe-form-field__error'>{vm.getBlockedDomainsError()}</div>}
                <p className='oe-form-field__info'>
                    <FormattedMessage
                        id='oe.registration.blockedDomainInfo'
                        description='Input info text'
                        defaultMessage={'Anyone with an email address ending in a domain on the Blocked List ' +
                            'will be unable to successfully register for this event.'}
                    />
                </p>
                <span className='oe-form-field__info'>
                    <FormattedMessage
                        id='oe.registration.blockedDomainRequirements'
                        description='Input info text'
                        defaultMessage='Please enter email domains without the @ symbol, eg openexc.com.  Enter one domain per line.'
                    />
                </span>
            </FormField>
        </div>
    );
});


export { GeneralRegistrationView };
